import React, { useCallback, useEffect, useState } from 'react';
import { Sun, Cloud, CloudSun, CloudFog, CloudDrizzle, CloudRain, Snowflake, CloudLightning, Wind, Droplets, Thermometer, Leaf, AlertTriangle, MapPin, RefreshCw } from 'lucide-react';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Button } from '../ui/button';
import { easerGet, fmtTime } from '../../lib/easerApi';

const ICONS = { sun: Sun, cloud: Cloud, 'cloud-sun': CloudSun, 'cloud-fog': CloudFog, 'cloud-drizzle': CloudDrizzle, 'cloud-rain': CloudRain, snowflake: Snowflake, 'cloud-lightning': CloudLightning };

/** Accueil: today + tomorrow forecast per client town visited (Open-Meteo), to plan outdoor jobs. */
export function EaserWeather({ onUnauthorized }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await easerGet('/api/easer/weather');
      setData(res.data);
      setError(false);
    } catch (err) {
      if (err?.response?.status === 401) return onUnauthorized?.();
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  return (
    <section data-testid="easer-weather">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-muted-foreground">Météo sur vos interventions</h3>
        <span className="text-[11px] text-muted-foreground" data-testid="weather-source">{data?.source || 'Open-Meteo'}{data?.days?.some((d) => d.places.some((p) => p.stale)) ? ' · prévision antérieure' : ''}</span>
      </div>
      {data?.unavailable && (
        <Card className="p-3 mb-2 text-sm flex items-center justify-between gap-2 border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5" data-testid="weather-unavailable">
          <span className="text-muted-foreground">Service météo momentanément injoignable depuis le serveur. Réessayez dans quelques minutes.</span>
          <Button size="sm" variant="outline" className="h-9 flex-shrink-0" onClick={load} disabled={loading} data-testid="weather-retry-unavailable"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </Card>
      )}
      {data === null && !error && <div className="grid grid-cols-2 gap-2"><Skeleton className="h-36 rounded-xl" /><Skeleton className="h-36 rounded-xl" /></div>}
      {error && data === null && (
        <Card className="p-4 text-sm flex items-center justify-between gap-2" data-testid="weather-error">
          <span className="text-muted-foreground">Météo indisponible pour le moment.</span>
          <Button size="sm" variant="outline" className="h-9" onClick={load} disabled={loading} data-testid="weather-retry"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Réessayer</Button>
        </Card>
      )}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {data.days.map((day) => (
            <Card key={day.date} className="p-3 space-y-2" data-testid={`weather-day-${day.label === 'Demain' ? 'tomorrow' : 'today'}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{day.label} · <span className="normal-case tracking-normal font-medium">{new Date(day.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })}</span></p>
              {day.places.map((p, i) => {
                const Icon = ICONS[p.icon] || Cloud;
                return (
                  <div key={`${p.town}-${i}`} className={`rounded-lg border p-2.5 ${p.available ? (p.outdoor_ok ? 'border-[hsl(var(--brand-sage))]/40 bg-[hsl(var(--brand-mint))]/40' : 'border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10') : 'border-border'}`} data-testid="weather-place">
                    <div className="flex items-start gap-2">
                      {p.available ? <Icon className="h-9 w-9 text-[hsl(var(--primary))] flex-shrink-0" aria-hidden="true" /> : <Cloud className="h-9 w-9 text-muted-foreground flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-sm flex items-center gap-1 leading-tight"><MapPin className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" /> <span className="truncate" data-testid="weather-town">{p.town}</span>{p.agency && <span className="text-[10px] font-normal text-muted-foreground">(agence)</span>}</p>
                        {p.available ? (
                          <>
                            <p className="text-sm leading-tight mt-0.5" data-testid="weather-label">{p.label} · <span className="font-semibold">{p.tmin}° / {p.tmax}°</span></p>
                            <p className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-2 gap-y-0.5">
                              <span className="inline-flex items-center gap-0.5"><Droplets className="h-3 w-3" /> pluie {p.rain_prob ?? '–'} %{p.rain_morning != null && p.rain_afternoon != null ? ` (matin ${p.rain_morning} %, après-midi ${p.rain_afternoon} %)` : ''}</span>
                              <span className="inline-flex items-center gap-0.5"><Wind className="h-3 w-3" /> {p.wind_max ?? '–'} km/h{p.gusts_max ? `, rafales ${p.gusts_max}` : ''}</span>
                              {p.rain_mm > 0 && <span className="inline-flex items-center gap-0.5"><Thermometer className="h-3 w-3" /> {p.rain_mm} mm</span>}
                            </p>
                          </>
                        ) : <p className="text-xs text-muted-foreground mt-0.5">Prévision indisponible pour cette ville.</p>}
                      </div>
                    </div>
                    {p.available && (
                      <p className={`text-xs mt-2 flex items-start gap-1 font-medium ${p.outdoor_ok ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--accent))]'}`} data-testid="weather-advice">
                        {p.outdoor_ok ? <Leaf className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /> : <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />} {p.advice}
                      </p>
                    )}
                    {p.events?.length > 0 && (
                      <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground" data-testid="weather-events">
                        {p.events.map((e) => <li key={e.id} className="truncate">{fmtTime(e.start)} · {e.client_name || e.label}{e.outdoor ? <span className="ml-1 text-[hsl(var(--primary))] font-medium">· extérieur</span> : ''}</li>)}
                      </ul>
                    )}
                  </div>
                );
              })}
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
